import React, { useState, useEffect, useMemo, useRef } from 'react';
import SubscriberHeader from '../Dashboard/components/Header/Header';
import Sidebar from '../Sidebar';
import './InvoiceAndPayments.css';
import DueSummary from './components/DueSummary/DueSummary';
import InvoiceSummary from './components/InvoiceSummary/InvoiceSummary';
import InvoiceHistory from './components/InvoiceHistory/InvoiceHistory';
import Payments from './components/Payments/Payments';
import MoreActions from './components/MoreActions/MoreActions';
import SubscriberInfoHeader from './components/SubscriberInfoHeader/SubscriberInfoHeader';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchTemplates, saveTemplate, getTemplateIdByName } from '../templateService';
import { getScenarioContent, getTemplatePrefix } from '../Constants/paymentScenarios';
import debounce from 'lodash.debounce';
import axios from 'axios';

// Default toggles for each card
const defaultToggles = {
  subscriberInfo: true,
  dueSummary: true,
  invoiceSummary: true,
  invoiceHistory: true,
  payments: true,
  moreActions: true,
  moreActionsSwitches: {  
    invoiceDeliveryToggle: true,
    reportToggle: true,
    upgradeServiceToggle: true,
    manageProfileToggle: true,
  },
};

// Safe parse template JSON
function safeParseTemplate(template) {
  try {
    let parsed = JSON.parse(template);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    return parsed;
  } catch (err) {
    console.error('Error parsing template:', err);
    return {};
  }
}

function InvoiceAndPayments({ activeData }) {
  const { token } = useAuth();
  const [toggles, setToggles] = useState(defaultToggles);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadTemplates, setReloadTemplates] = useState(false);
  const triggerSaveData = useRef(false);

  // Payment scenario integration - matching UsageAnalysis pattern
  const paymentScenario = activeData || null;
  console.log('InvoiceAndPayments - Received paymentScenario:', paymentScenario);

  // Use constants instead of hardcoded logic
  const scenarioContent = getScenarioContent(paymentScenario);
  const scenarioPrefix = getTemplatePrefix(paymentScenario);

  console.log('InvoiceAndPayments activeData:', activeData);
  console.log('InvoiceAndPayments scenarioPrefix:', scenarioPrefix);

  // Template name logic with scenario prefix - matching UsageAnalysis pattern
  const template_name = scenarioPrefix === 'current-payment' 
    ? 'gardenwall-invoiceAndPayments' 
    : `gardenwall-invoiceAndPayments-${scenarioPrefix}`;
    
  console.log('InvoiceAndPayments Template name:', template_name);

  // Ref to store latest templates for debounce
  const templatesRef = useRef(templates);
  useEffect(() => {
    templatesRef.current = templates;
  }, [templates]);

  // Fetch templates function - matching UsageAnalysis
  const fetchTemplatesData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/templates/list`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        }
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching templates:', error);
      return [];
    }
  };

  // Load templates on mount - matching UsageAnalysis
  useEffect(() => {
    const loadTemplates = async () => {
      const fetchedTemplates = await fetchTemplatesData();
      setTemplates(fetchedTemplates);
    };

    loadTemplates();
  }, []);

  // Reload templates effect - matching UsageAnalysis
  useEffect(() => {
    if (reloadTemplates) {
      const loadTemplates = async () => {
        const fetchedTemplates = await fetchTemplatesData();
        setTemplates(fetchedTemplates);
        setReloadTemplates(false);
      };
  
      loadTemplates();
    }
  }, [reloadTemplates]);

  // Apply template data when templates are loaded - matching UsageAnalysis
  useEffect(() => {
    if (templates && templates.length > 0) {
      console.log('InvoiceAndPayments Templates updated:', templates);
      const specificTemplate = templates.find(
        (template) => template.template_name === template_name
      );
      if (specificTemplate) {
        try {
          const template_data = JSON.parse(
            JSON.parse(specificTemplate.template)
          );
          setToggles(template_data.toggles || defaultToggles);
        } catch (error) {
          console.error('Error parsing template data:', error);
        }
      } else {
        console.log('InvoiceAndPayments specific template not found');
      }
    }
  }, [templates, template_name]);

  // Auto-save trigger - matching UsageAnalysis pattern
  useEffect(() => {
    if (triggerSaveData.current) {
      saveData();
      triggerSaveData.current = false;
    }
  }, [toggles]);

  // Save data function - matching UsageAnalysis pattern
  const saveData = async () => {
    const formData = new FormData();
    formData.append('template_name', template_name);
    formData.append(
      'template',
      JSON.stringify({
        toggles,
        scenarioData: paymentScenario,
      })
    );
    
    console.log('InvoiceAndPayments - Saving template with name:', template_name);

    const urlBase = `${process.env.REACT_APP_BASE_URL}/templates`;
    let url = `${urlBase}/create`;
    let method = 'post';

    if (templates && templates.length > 0) {
      const specificTemplate = templates.find(
        (template) => template.template_name === template_name
      );
      if (specificTemplate && specificTemplate.id) {
        url = `${urlBase}/update/${specificTemplate.id}`;
        method = 'put';
      }
    }

    try {
      const response = await axios({
        method: method,
        url: url,
        data: formData,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${token}`,
        },
      });
      console.log('InvoiceAndPayments Response Data:', response.data); 
      if (method === 'post') {
        setReloadTemplates(true);
      }

    } catch (error) {
      console.error('Error saving invoice and payments template:', error);
    }
  };

  // Toggle handlers - updated to use triggerSaveData pattern
  const handleToggle = (section) => {
    console.log(`Toggling: ${section}, current value: ${toggles[section]}`);

    setToggles(prev => ({ 
      ...prev, 
      [section]: !prev[section]
    }));
    triggerSaveData.current = true;
  };
  
  const handleMoreActionSwitch = (id, value) => {
    setToggles(prev => ({
      ...prev,
      moreActionsSwitches: {
        ...prev.moreActionsSwitches,
        [id]: value,
      },
    }));
    triggerSaveData.current = true;
  };
  
  // Memoized switches for MoreActions
  const memoizedSwitches = useMemo(() => toggles.moreActionsSwitches, [toggles.moreActionsSwitches]);

  return (
    <div className="subscriberConnect-container">
      {/* Invoice and Payments Header with Payment Scenario Status */}
      <h1 className="invoice-payments-header">
        Invoice and Payments – <span className={scenarioContent.headerClass}>{scenarioContent.headerStatus}</span>
      </h1>
  
      {/* Subscriber Info Header - OUTSIDE the grid with toggle */}
      <SubscriberInfoHeader 
        hidden={!toggles.subscriberInfo}
        checked={toggles.subscriberInfo}
        onChange={() => handleToggle('subscriberInfo')}
        subscriberData={{
          name: 'Herman Hoff',
          subscriberId: '92550111078',
          email: 'joy1103@yahoo.com',
          currentPlan: 'Internet Service 50/50',
          accounts: [
            { id: '92554171828', number: '92551154622' }
          ],
          selectedAccount: '92554171828 - 92551154622'
        }}
      />

      <div className="subscriber-connect-header">
        <div id="invoice-grid">
          <DueSummary 
            hidden={!toggles.dueSummary}
            checked={toggles.dueSummary}
            onChange={() => handleToggle('dueSummary')}
            scenarioData={paymentScenario}
          />
  
          <InvoiceSummary
            hidden={!toggles.invoiceSummary}
            checked={toggles.invoiceSummary}
            onChange={() => handleToggle('invoiceSummary')}
            scenarioData={paymentScenario}
          />
  
          <InvoiceHistory
            hidden={!toggles.invoiceHistory}
            checked={toggles.invoiceHistory}
            onChange={() => handleToggle('invoiceHistory')}
            scenarioData={paymentScenario}
          />
  
          <Payments
            hidden={!toggles.payments}
            checked={toggles.payments}
            onChange={() => handleToggle('payments')}
            scenarioData={paymentScenario}
          />
  
          <MoreActions
            hidden={!toggles.moreActions}
            checked={toggles.moreActions}
            onChange={() => handleToggle('moreActions')}
            switches={memoizedSwitches}
            onSwitchChange={handleMoreActionSwitch}
            scenarioData={paymentScenario}
          />
        </div>
      </div>
    </div>
  );
}

export default InvoiceAndPayments;

